import { createFileRoute, useBlocker, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { Clock, Users, UploadCloud } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
// Write flow — both the entry-card screen and the wizard need to be Operator-gated
// at the route level. The Sidebar/Header entries that link here already filter on
// requiredPermission: 'write' (Sidebar.tsx, Header.tsx), but a Viewer who types the
// URL bypasses the menu filter; without this guard they'd see the wizard form and
// only hit a 403 when they POST. Must stay in lockstep with both nav configs.
import { OperatorGuard } from '@/components/guards/OperatorGuard';
import { WizardContainer } from '@/components/wizard/WizardContainer';
import { OrganizationTypeStep } from '@/components/wizard/steps/OrganizationTypeStep';
import { BridgeLookupStep } from '@/components/wizard/steps/BridgeLookupStep';
import { ModuleSelectionStep } from '@/components/wizard/steps/ModuleSelectionStep';
import { ValidationStep } from '@/components/wizard/steps/ValidationStep';
import { ConfirmationStep } from '@/components/wizard/steps/ConfirmationStep';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useWizardStore, useWizardActions } from '@/stores';
import { useStartMigration } from '@/features/migrations/hooks';
import type { WizardStepId } from '@/lib/validations';
import { cn } from '@/lib/utils';

interface NewMigrationSearch {
  type?: string | undefined;
}

export const Route = createFileRoute('/migrations/new')({
  validateSearch: (search: Record<string, unknown>): NewMigrationSearch => ({
    type: typeof search.type === 'string' ? search.type : undefined,
  }),
  component: NewMigrationPage,
});

// ---------------------------------------------------------------------------
// Entry screen — shown when no ?type param is present
// ---------------------------------------------------------------------------

function MigrationTypeEntry() {
  const navigate = useNavigate();

  const handleSingle = () => {
    void navigate({ to: '/migrations/new', search: { type: 'single' } });
  };

  const handleMass = () => {
    void navigate({ to: '/migrations/mass' });
  };

  return (
    <AppLayout>
      <div className="container max-w-3xl py-12">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Start a Migration</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Choose how you would like to migrate organizations to Amplify.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {/* Single Customer card */}
          <button
            type="button"
            onClick={handleSingle}
            className={cn(
              'w-full rounded-lg border-2 border-border bg-card p-0 text-left',
              'transition-colors hover:border-primary/50 hover:bg-accent/40',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            )}
            aria-label="Start a single customer migration"
          >
            <Card className="border-0 shadow-none bg-transparent">
              <CardHeader>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary">
                  <Users className="h-5 w-5 text-secondary-foreground" aria-hidden="true" />
                </div>
                <CardTitle className="mt-3 text-base">Single Customer</CardTitle>
                <CardDescription>
                  Migrate one organization at a time through our guided 5-step process.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>Estimated time: ~15 min</span>
                </div>
              </CardContent>
            </Card>
          </button>

          {/* Mass Migration card */}
          <button
            type="button"
            onClick={handleMass}
            className={cn(
              'w-full rounded-lg border-2 border-primary bg-card p-0 text-left',
              'transition-colors hover:bg-primary/5',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            )}
            aria-label="Start a mass migration"
          >
            <Card className="border-0 shadow-none bg-transparent">
              <CardHeader>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <UploadCloud className="h-5 w-5 text-primary" aria-hidden="true" />
                </div>
                <CardTitle className="mt-3 text-base">Mass Migration</CardTitle>
                <CardDescription>
                  Upload a CSV to migrate multiple organizations in a single coordinated batch.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>Estimated time: ~45 min depending on size</span>
                </div>
              </CardContent>
            </Card>
          </button>
        </div>
      </div>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Existing single-migration wizard (unchanged logic)
// ---------------------------------------------------------------------------

function SingleMigrationWizard() {
  const step = useWizardStore((s) => s.step);
  const isValid = useWizardStore((s) => s.isValid);
  const organizationType = useWizardStore((s) => s.organizationType);
  const organizationData = useWizardStore((s) => s.organizationData);
  const selectedModules = useWizardStore((s) => s.selectedModules);
  const bridgeEntity = useWizardStore((s) => s.bridgeEntity);
  const bridgeEntityUuid = useWizardStore((s) => s.bridgeEntityUuid);
  const matchedModules = useWizardStore((s) => s.matchedModules);
  const modulePropertyOverrides = useWizardStore((s) => s.modulePropertyOverrides);
  const userMigrationEnabled = useWizardStore((s) => s.userMigrationEnabled);
  const { reset } = useWizardActions();
  const navigate = useNavigate();

  const submittedRef = useRef(false);
  const isDirtyRef = useRef(false);
  isDirtyRef.current =
    !submittedRef.current &&
    (organizationType !== null || bridgeEntityUuid !== '' || selectedModules.length > 0);

  useEffect(() => {
    if (!isDirtyRef.current) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [organizationType, bridgeEntityUuid, selectedModules]);

  const blocker = useBlocker({
    shouldBlockFn: () => !submittedRef.current && isDirtyRef.current,
    withResolver: true,
  });

  const { mutate: startMigration, isPending: isSubmitting } = useStartMigration({
    onSuccess: (result) => {
      submittedRef.current = true;
      reset();
      void navigate({ to: '/migrations/$migrationId', params: { migrationId: result.migrationId } });
    },
    onError: (error) => {
      console.error('Failed to start migration:', error);
    },
  });

  const stepValidation: Record<WizardStepId, boolean> = {
    1: organizationType !== null && isValid,
    2: bridgeEntity !== undefined && matchedModules.length > 0 && isValid,
    3: selectedModules.length > 0 && isValid,
    4: isValid,
    5: isValid,
  };

  const handleSubmit = () => {
    if (!organizationData || !bridgeEntityUuid || selectedModules.length === 0) return;

    const modulesWithBridge = selectedModules.map((selected) => {
      const matched = matchedModules.find((m) => m.moduleKey === selected.moduleKey);
      const overrides = modulePropertyOverrides[selected.moduleKey];
      return {
        moduleKey: selected.moduleKey,
        sourceId: selected.sourceId,
        amplifyModuleId: matched?.amplifyModuleId ?? 0,
        bridgeProductId: matched?.bridgeProductId,
        configurationOverrides: overrides?.length ? overrides : undefined,
        userMigrationEnabled: userMigrationEnabled[selected.moduleKey] ?? false,
      };
    });

    startMigration({
      organizationType: organizationData.organizationType,
      bridgeEntityUuid: bridgeEntityUuid,
      bridgeEntityName: bridgeEntity?.name,
      organizationName: organizationData.organizationName,
      amplifyOrganizationUuid: organizationData.amplifyOrganizationUuid,
      streetAddress: organizationData.streetAddress,
      addressLine2: organizationData.addressLine2,
      city: organizationData.city,
      state: organizationData.state,
      zipCode: organizationData.zipCode,
      ownerFirstName: organizationData.ownerFirstName,
      ownerLastName: organizationData.ownerLastName,
      ownerEmail: organizationData.ownerEmail,
      ownerPhone: organizationData.ownerPhone,
      billing: organizationData.billing,
      sendInvite: organizationData.sendInvite ?? false,
      euid: organizationData.euid || undefined,
      modules: modulesWithBridge,
    });
  };

  const handleLeaveConfirm = () => {
    reset();
    blocker.proceed?.();
  };

  const handleLeaveCancel = () => {
    blocker.reset?.();
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return <OrganizationTypeStep />;
      case 2:
        return <BridgeLookupStep />;
      case 3:
        return <ModuleSelectionStep />;
      case 4:
        return <ValidationStep />;
      case 5:
        return <ConfirmationStep />;
      default:
        return <OrganizationTypeStep />;
    }
  };

  return (
    <AppLayout>
      <WizardContainer
        stepValidation={stepValidation}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      >
        {renderStep()}
      </WizardContainer>

      <Dialog open={blocker.status === 'blocked'} onOpenChange={() => handleLeaveCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave wizard?</DialogTitle>
            <DialogDescription>
              You have unsaved progress in the migration wizard. Leaving will discard all entered
              data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleLeaveCancel}>
              Stay
            </Button>
            <Button variant="destructive" onClick={handleLeaveConfirm}>
              Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Route component — dispatches based on ?type search param
// ---------------------------------------------------------------------------

function NewMigrationPage() {
  const { type } = Route.useSearch();

  return (
    <OperatorGuard>
      {type === 'single' ? <SingleMigrationWizard /> : <MigrationTypeEntry />}
    </OperatorGuard>
  );
}
